/**
 * AddLeaderHandle - Handle for adding leader lines.
 *
 * For MText: Converts MText to MLeader when dragged.
 * For MLeader: Adds another leader line to the existing MLeader.
 *
 * Appears as a green circle at the right edge of selected text entity.
 * When dragged, shows a preview polyline with arrow head.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import { trackShapeChange } from "@/analytics/drawing-analytics";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D } from "../types";
import {
  HandleType,
  HandlePosition,
  ADD_LEADER_HANDLE_DRAW_OPTIONS,
  ADD_LEADER_HANDLE_OFFSET_PIXELS,
  HANDLE_SIZES,
} from "../types";
import { AbstractHandle } from "./AbstractHandle";
import { ConvertMTextToMLeaderCommand } from "../../commands/ConvertMTextToMLeaderCommand";
import { AddLeaderLineCommand } from "../../commands/LeaderLineCommand";
import {
  getTextEntityProperties,
  calculateLeaderVertices,
  type Point3D as LeaderPoint3D,
  type TextEntityProperties,
} from "../../leaders/conversion";
import { selectionService } from "../../selection";

/**
 * Handle for adding a leader line to MText or MLeader.
 */
export class AddLeaderHandle extends AbstractHandle {
  readonly type = HandleType.AddLeader;
  readonly position = HandlePosition.AddLeader;

  /** Entity handle string */
  protected entityHandle: string = "";

  /** Cached text entity properties (works for both MText and MLeader) */
  protected textProps: TextEntityProperties | null = null;

  /** Preview overlay IDs */
  private previewLineId: string | null = null;
  private previewArrowId: string | null = null;

  /** Fixed pixel offset floor, converted to world units at current zoom */
  private offsetInPx: number =
    ADD_LEADER_HANDLE_OFFSET_PIXELS * HANDLE_SIZES.handleScale;
  private worldOffset = 0;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityId: DrawingModule.OdDbObjectId,
  ) {
    super(viewer, history);
    this.drawOptions = { ...ADD_LEADER_HANDLE_DRAW_OPTIONS };

    // Store entity handle
    this.entityHandle = this.getEntityHandle(entityId);

    // Get text properties (works for both MText and MLeader)
    this.textProps = getTextEntityProperties(viewer, entityId);

    if (this.textProps) {
      // Position handle at right edge + offset (1.5 * textHeight, floored to a
      // fixed screen-pixel distance so it stays visible when zoomed out)
      this.worldOffset = this.computeWorldOffset();
      const textCenterY =
        (this.textProps.textExtent.min.y + this.textProps.textExtent.max.y) / 2;
      this.worldPosition = {
        x: this.textProps.textExtent.max.x + this.worldOffset,
        y: textCenterY,
        z: 0,
      };
    }
  }

  /**
   * Offset from the text's right edge: proportional to text height at normal
   * zoom, floored to a fixed screen-pixel distance when zoomed out.
   */
  private computeWorldOffset(): number {
    const pixelFloor = this.viewer.screenDistanceToWorld(this.offsetInPx);
    const proportional = this.textProps ? 1.5 * this.textProps.textHeight : 0;
    return Math.max(pixelFloor, proportional);
  }

  /**
   * Recompute the offset on redraw so it keeps a constant screen-pixel
   * distance from the text edge regardless of zoom (same pattern as
   * RotationHandle/ScaleHandle).
   */
  beforeRedraw(): void {
    if (!this.textProps) return;
    const previousWorldOffset = this.worldOffset;
    this.worldOffset = this.computeWorldOffset();
    this.worldPosition.x += this.worldOffset - previousWorldOffset;
  }

  /**
   * Start - initialize preview.
   */
  start(): void {
    if (this.isTransforming) return;
    this.isTransforming = true;

    // Create preview overlays
    this.createPreview();
  }

  /**
   * Ongoing - update preview line to follow cursor.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming || !this.textProps) return;

    this.updatePreview(context.worldPoint);
  }

  /**
   * End - execute conversion or add leader line.
   */
  end(context: TransformContext): void {
    if (!this.isTransforming || !this.textProps) {
      console.warn(
        "[AddEntityLeader] text properties are not defined. Can't create MLeader",
      );
      this.cleanupPreview();
      this.isTransforming = false;
      return;
    }

    this.isTransforming = false;

    // Clean up preview
    this.cleanupPreview();

    // Calculate leader vertices based on arrow tip position
    const arrowTip: LeaderPoint3D = {
      x: context.worldPoint.x,
      y: context.worldPoint.y,
      z: 0,
    };

    const vertices = calculateLeaderVertices(
      this.textProps.textExtent,
      this.textProps.textHeight,
      arrowTip,
    );

    // Check if there was actual movement (minimum distance)
    const textCenterX =
      (this.textProps.textExtent.min.x + this.textProps.textExtent.max.x) / 2;
    const textCenterY =
      (this.textProps.textExtent.min.y + this.textProps.textExtent.max.y) / 2;
    const minDistance = this.textProps.textHeight * 2;
    const dist = Math.sqrt(
      Math.pow(arrowTip.x - textCenterX, 2) +
        Math.pow(arrowTip.y - textCenterY, 2),
    );

    if (dist < minDistance) {
      // Not enough movement - don't create/add leader
      return;
    }

    if (this.textProps.isMLeader) {
      // Add leader line to existing MLeader (with undo/redo support)
      trackShapeChange("leader_added");
      const command = new AddLeaderLineCommand(this.viewer, {
        mleaderHandle: this.entityHandle,
        arrowTip,
      });

      this.history.execute(command);

      // Refresh selection to update handles
      selectionService.updateGrid();
    } else {
      // Convert MText to MLeader
      trackShapeChange("leader_added");
      const command = new ConvertMTextToMLeaderCommand(this.viewer, {
        mtextHandle: this.entityHandle,
        originalLayerName: this.textProps.layerName,
        mleaderOptions: {
          textContent: this.textProps.textContent,
          textHeight: this.textProps.textHeight,
          textLocation: this.textProps.textLocation,
          textExtent: this.textProps.textExtent,
          layerName: this.textProps.layerName,
          vertices,
          textStyleHandle: this.textProps.textStyleHandle,
          attachmentPoint: this.textProps.attachmentPoint,
          entityColor: this.textProps.entityColor,
        },
      });

      this.history.execute(command);
    }

    this.viewer.update();
  }

  /**
   * Cancel - clean up preview.
   */
  cancel(): void {
    if (!this.isTransforming) return;

    this.cleanupPreview();
    this.isTransforming = false;
  }

  // ============== Preview Methods ==============

  /**
   * Create preview overlays.
   */
  private createPreview(): void {
    if (!this.textProps) return;

    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    // Initial preview line from text center to handle position
    const textCenterY =
      (this.textProps.textExtent.min.y + this.textProps.textExtent.max.y) / 2;
    const startScreen = this.viewer.worldToScreen({
      x: this.textProps.textExtent.max.x,
      y: textCenterY,
      z: 0,
    });
    const endScreen = this.viewer.worldToScreen(this.worldPosition);

    this.previewLineId = overlay.createPolyline([startScreen, endScreen], {
      strokeColor: "rgb(50, 180, 80)",
      strokeWidth: 2,
      lineDash: [4, 4],
    });
  }

  /**
   * Update preview to follow cursor.
   */
  private updatePreview(worldPoint: Point3D): void {
    if (!this.textProps) return;

    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    // Calculate leader vertices
    const arrowTip: LeaderPoint3D = { x: worldPoint.x, y: worldPoint.y, z: 0 };
    const [landingPoint, doglegPoint] = calculateLeaderVertices(
      this.textProps.textExtent,
      this.textProps.textHeight,
      arrowTip,
    );

    // Convert to screen coordinates
    const screenLanding = this.viewer.worldToScreen(landingPoint);
    const screenDogleg = this.viewer.worldToScreen(doglegPoint);
    const screenArrow = this.viewer.worldToScreen(worldPoint);

    // Update preview line
    if (this.previewLineId) {
      overlay.update(this.previewLineId, {
        points: [screenLanding, screenDogleg, screenArrow],
      });
    }

    // Update or create arrow head preview
    const arrowHeadPoints = this.computeArrowHead(doglegPoint, arrowTip);
    const screenArrowHead = arrowHeadPoints.map((p) =>
      this.viewer.worldToScreen(p),
    );

    if (this.previewArrowId) {
      overlay.update(this.previewArrowId, {
        points: [...screenArrowHead, screenArrowHead[0]], // Close the triangle
      });
    } else {
      this.previewArrowId = overlay.createPolyline(
        [...screenArrowHead, screenArrowHead[0]],
        {
          strokeColor: "rgb(50, 180, 80)",
          strokeWidth: 2,
          fillColor: "rgb(50, 180, 80)",
        },
      );
    }
  }

  /**
   * Clean up preview overlays.
   */
  private cleanupPreview(): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (this.previewLineId) {
      overlay.remove(this.previewLineId);
      this.previewLineId = null;
    }
    if (this.previewArrowId) {
      overlay.remove(this.previewArrowId);
      this.previewArrowId = null;
    }
  }

  /**
   * Compute arrow head triangle points.
   */
  private computeArrowHead(
    tailPoint: LeaderPoint3D,
    tipPoint: LeaderPoint3D,
  ): [Point3D, Point3D, Point3D] {
    const textHeight = this.textProps?.textHeight ?? 1;
    const size = 0.6 * textHeight;

    const angle = Math.atan2(
      tipPoint.y - tailPoint.y,
      tipPoint.x - tailPoint.x,
    );
    const delta = (15 * Math.PI) / 180; // 15 degree angle for arrow head

    const point1: Point3D = {
      x: tipPoint.x - size * Math.cos(angle + delta),
      y: tipPoint.y - size * Math.sin(angle + delta),
      z: 0,
    };

    const point2: Point3D = {
      x: tipPoint.x - size * Math.cos(angle - delta),
      y: tipPoint.y - size * Math.sin(angle - delta),
      z: 0,
    };

    return [point1, point2, tipPoint];
  }
}
